import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { useProducts } from "@/context/ProductsContext";
import { api, API } from "@/lib/api";
import { formatPrice } from "@/data/products";
import {
  Package, Calendar, Image as ImageIcon, LayoutGrid, Layers,
  PanelTop, Menu as MenuIcon, Settings2, ShoppingBag, Sparkles,
  ArrowUpRight, ArrowRight, Plus, BookOpen
} from "lucide-react";

const abs = (url) => (!url ? url : url.startsWith("http") ? url : url.startsWith("/api/") ? API.replace(/\/api$/, "") + url : url);

const CARDS = [
  { to: "/admin/site", label: "Site & Logo", desc: "Upload logo, edit home copy and toggle sections", icon: Settings2 },
  { to: "/admin/nav", label: "Navigation", desc: "Reorder header links, add / rename / hide", icon: MenuIcon },
  { to: "/admin/hero", label: "Hero Slides", desc: "Editorial slideshow on the storefront", icon: PanelTop },
  { to: "/admin/products", label: "Product CMS", desc: "Add, edit, price and pin every piece", icon: LayoutGrid },
  { to: "/admin/categories", label: "Categories", desc: "Rename, reorder and re-cover collections", icon: Layers },
  { to: "/admin/journal", label: "Journal", desc: "Write, edit and reorder editorial stories", icon: BookOpen },
  { to: "/admin/media", label: "Media Library", desc: "Upload campaign photos, get URLs", icon: ImageIcon },
  { to: "/admin/orders", label: "Orders & Leads", desc: "Every order and bridal appointment", icon: ShoppingBag },
];

export default function AdminDashboard() {
  const { user } = useAuth();
  const { settings } = useSite();
  const { products, categories } = useProducts();
  const [orders, setOrders] = useState([]);
  const [appts, setAppts] = useState([]);
  const [media, setMedia] = useState([]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    Promise.all([
      api.get("/admin/orders").catch(() => ({ data: [] })),
      api.get("/admin/appointments").catch(() => ({ data: [] })),
      api.get("/media").catch(() => ({ data: [] })),
    ]).then(([o, a, m]) => { setOrders(o.data); setAppts(a.data); setMedia(m.data); });
  }, [user]);

  if (user === undefined) return <div className="pt-40 container-lux">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  if (user.role !== "admin") return (
    <div className="pt-40 container-lux min-h-[60vh]">
      <h1 className="font-serif text-4xl">Admin access required</h1>
    </div>
  );

  const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
  const pendingApts = appts.filter(a => a.status === "pending").length;
  const paidOrders = orders.filter(o => o.payment_status === "paid").length;

  const stats = [
    { label: "Revenue", value: formatPrice(revenue), sub: `${orders.length} order${orders.length===1?"":"s"}`, testid: "d-rev" },
    { label: "Paid Orders", value: paidOrders, sub: `${orders.length - paidOrders} pending`, testid: "d-paid" },
    { label: "Bridal Leads", value: pendingApts, sub: `${appts.length} total`, testid: "d-leads", accent: pendingApts > 0 },
    { label: "Live Products", value: products.filter(p => p.is_active !== false).length, sub: `${categories.length} categories`, testid: "d-prod" },
  ];

  return (
    <div className="pt-32 pb-24 bg-ivory min-h-screen" data-testid="admin-dashboard">
      {/* Header */}
      <div className="container-lux">
        <p className="overline text-muted-foreground">Atelier · Admin</p>
        <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <h1 className="font-serif tracking-tighter text-5xl md:text-7xl font-light">Bonjour, <em className="not-italic">{user.name.split(" ")[0]}</em></h1>
            <p className="mt-4 text-sm text-muted-foreground max-w-xl">Every corner of the maison in one place — the storefront, the atelier's photography, every piece and every bride's enquiry.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/" className="lux-btn-outline py-3 px-5 text-[10px]">View Storefront</Link>
            <Link to="/admin/products" className="lux-btn-dark py-3 px-5 text-[10px] inline-flex items-center gap-2" data-testid="d-new-product"><Plus size={12} strokeWidth={1} /> New Piece</Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container-lux mt-14">
        <div className="grid grid-cols-2 md:grid-cols-4 border border-border">
          {stats.map(s => (
            <div key={s.label} data-testid={s.testid}
              className={`p-6 md:p-8 border-r border-border last:border-r-0 border-b md:border-b-0 ${s.accent ? "bg-ink text-ivory" : ""}`}>
              <p className={`overline ${s.accent ? "text-ivory/70" : "text-muted-foreground"}`}>{s.label}</p>
              <p className="font-serif text-3xl md:text-4xl tracking-tighter mt-4">{s.value}</p>
              <p className={`text-xs mt-2 ${s.accent ? "text-ivory/60" : "text-muted-foreground"}`}>{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="container-lux mt-20">
        <div className="flex items-baseline justify-between">
          <h2 className="font-serif tracking-tighter text-3xl md:text-4xl font-light">Manage The Maison</h2>
          <span className="overline text-muted-foreground hidden md:inline">{CARDS.length} sections</span>
        </div>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {CARDS.map(c => {
            const Icon = c.icon;
            return (
              <Link key={c.to} to={c.to} data-testid={`d-card-${c.to.split('/').pop()}`}
                className="group relative border border-border p-7 hover:bg-ink hover:text-ivory hover:border-ink transition-colors duration-500 flex flex-col">
                <Icon size={24} strokeWidth={0.8} />
                <p className="mt-6 font-serif text-2xl tracking-tighter">{c.label}</p>
                <p className="mt-2 text-xs text-muted-foreground group-hover:text-ivory/60 leading-relaxed">{c.desc}</p>
                <span className="absolute top-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity"><ArrowUpRight size={18} strokeWidth={1} /></span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Recent activity */}
      <div className="container-lux mt-20 grid lg:grid-cols-2 gap-10">
        <ActivityBlock title="Recent Orders" empty="No orders yet." data={orders.slice(0, 5)} link="/admin/orders" render={(o) => (
          <div className="flex items-center justify-between border-b border-border py-3 gap-4">
            <div className="min-w-0">
              <p className="font-serif text-lg truncate">{o.customer?.name || "Guest"}</p>
              <p className="text-[11px] text-muted-foreground mt-1">#{o.id} · {new Date(o.created_at).toLocaleDateString()}</p>
            </div>
            <div className="text-right">
              <p className="font-serif">{formatPrice(o.total)}</p>
              <p className={`text-[10px] uppercase tracking-widest mt-1 ${o.payment_status === "paid" ? "text-ink" : "text-muted-foreground"}`}>{o.payment_status}</p>
            </div>
          </div>
        )} />

        <ActivityBlock title="Bridal Enquiries" empty="No enquiries yet." data={appts.slice(0, 5)} link="/admin/orders" render={(a) => (
          <div className="flex items-center justify-between border-b border-border py-3 gap-4">
            <div className="min-w-0">
              <p className="font-serif text-lg truncate">{a.name}</p>
              <p className="text-[11px] text-muted-foreground mt-1">{a.event_date} · {a.city}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-1 border ${a.status === "pending" ? "border-ink" : "border-border text-muted-foreground"}`}>{a.status}</span>
          </div>
        )} />
      </div>

      {/* Recent products & media */}
      <div className="container-lux mt-20 grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-baseline justify-between">
            <p className="overline">Latest Pieces</p>
            <Link to="/admin/products" className="lux-link">Manage <ArrowRight size={11} className="inline ml-1" strokeWidth={1} /></Link>
          </div>
          <ul className="mt-6 grid grid-cols-4 gap-3">
            {products.slice(0, 8).map(p => (
              <li key={p.id} className="group">
                <Link to={`/product/${p.slug}?preview=1`} target="_blank" rel="noreferrer" className="block aspect-[3/4] bg-muted overflow-hidden">
                  {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />}
                </Link>
                <p className="mt-2 text-xs truncate">{p.title}</p>
                <p className="text-[10px] text-muted-foreground">{formatPrice(p.price)}</p>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <p className="overline">Library · {media.length} files</p>
            <Link to="/admin/media" className="lux-link">Upload <ArrowRight size={11} className="inline ml-1" strokeWidth={1} /></Link>
          </div>
          <ul className="mt-6 grid grid-cols-4 gap-3">
            {media.slice(0, 8).map(m => (
              <li key={m.id} className="aspect-square bg-muted overflow-hidden">
                <img src={`${API}/media/${m.id}`} alt="" className="w-full h-full object-cover" loading="lazy" />
              </li>
            ))}
            {media.length === 0 && <li className="col-span-4 border border-dashed border-border p-8 text-center text-xs text-muted-foreground">Library empty — <Link to="/admin/media" className="underline">upload your first photo</Link>.</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}

function ActivityBlock({ title, data, empty, link, render }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <p className="overline">{title}</p>
        <Link to={link} className="lux-link">View all <ArrowRight size={11} className="inline ml-1" strokeWidth={1} /></Link>
      </div>
      <div className="mt-4">
        {data.length === 0 ? <p className="text-sm text-muted-foreground py-6">{empty}</p> : data.map((d, i) => <div key={d.id || i}>{render(d)}</div>)}
      </div>
    </div>
  );
}
